import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import services from '../../services'
import { useAuth } from '../../hooks/useAuth'
import { useNotification } from '../../hooks/useNotification.js'
import Janela from '../componentes/Janela'
import CampoBusca from './CampoBusca'
import ProcessarSolicitacao from './ProcessarSolicitacao'
import {
  Acompanhante, Anexo, atrasada, conferirArquivo, corpoDaTriagem, dataDoPrazo, dataHora, Detalhe, encerrado, ESFORCOS, FormTriagem, Gestor, Historico, linhasDoPedido, soAcompanha, MAX_ANEXOS, Mensagem, motivoValido, Opcao,
  podeAceitarOuRecusar, podeProcessar, podeReabrir, PRIORIDADES, ResultadoProcessar, rotuloPrioridade, rotuloStatus, rotuloTipo, STATUS, statusParaEscolher, tamanhoLegivel, textoDoHistorico, triagemDe
} from './comum'
import '../componentes/vinculo.scss'
import './solicitacoes.scss'

interface Props {
  /** Id aberto, ou null com a janela fechada. */
  id: number | null
  aoFechar: () => void
}

type Item = { quando: string; msg?: Mensagem; hist?: Historico }

type AcaoComMotivo = 'cancelar' | 'recusar' | 'reabrir' | 'bloquear'

const JANELA_DO_MOTIVO: Record<AcaoComMotivo, { titulo: string; rotulo: string; botao: string }> = {
  cancelar: { titulo: 'Cancelar solicitação', rotulo: 'Motivo do cancelamento (quem pediu vai ver)', botao: 'Cancelar solicitação' },
  recusar: { titulo: 'Recusar entrega', rotulo: 'O que precisa ser ajustado', botao: 'Recusar entrega' },
  reabrir: { titulo: 'Reabrir solicitação', rotulo: 'Motivo (opcional)', botao: 'Reabrir' },
  bloquear: { titulo: 'Bloquear solicitação', rotulo: 'Motivo do bloqueio', botao: 'Bloquear' }
}

/** A solicitação por inteiro: o pedido, os anexos, a conversa e o histórico na mesma linha do tempo. */
export default function SolicitacaoDetalhe({ id, aoFechar }: Props) {
  const { getUser } = useAuth()
  const { notify } = useNotification()
  const euId: number | undefined = getUser()?.id
  const [d, setD] = useState<Detalhe | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [mensagens, setMensagens] = useState<Mensagem[]>([])
  const [historico, setHistorico] = useState<Historico[]>([])
  const [anexos, setAnexos] = useState<Anexo[]>([])
  const [acompanhantes, setAcompanhantes] = useState<Acompanhante[]>([])
  const [texto, setTexto] = useState('')
  const [interna, setInterna] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [subindo, setSubindo] = useState(false)
  const [gestores, setGestores] = useState<Gestor[]>([])
  const [agindo, setAgindo] = useState(false)
  const [motivoPara, setMotivoPara] = useState<AcaoComMotivo | null>(null)
  const [motivo, setMotivo] = useState('')
  const [processando, setProcessando] = useState(false)
  const [triagem, setTriagem] = useState<FormTriagem>({ prioridade: 'normal', prazo: '', esforco: '' })
  const seletor = useRef<HTMLInputElement>(null)

  const carregarConversa = useCallback(async (alvo: number) => {
    const [m, h, a] = await Promise.all([
      services.api.solicitacaoMensagens(alvo), services.api.solicitacaoHistorico(alvo), services.api.solicitacaoAnexos(alvo)
    ])
    if (!m.fail) setMensagens(m.data)
    if (!h.fail) setHistorico(h.data)
    if (!a.fail) setAnexos(a.data)
  }, [])

  const carregarAcompanhantes = useCallback(async (alvo: number) => {
    const r = await services.api.solicitacaoAcompanhantes(alvo)
    if (!r.fail) setAcompanhantes(r.data)
  }, [])

  useEffect(() => {
    if (id == null) return
    setD(null); setErro(null); setMensagens([]); setHistorico([]); setAnexos([]); setAcompanhantes([]); setTexto(''); setInterna(false)
    setGestores([]); setMotivoPara(null); setMotivo(''); setProcessando(false)
    let vivo = true
    services.api.solicitacaoDetalhe(id).then((r: any) => {
      if (!vivo) return
      if (r.fail) { setErro(r.error || 'Não foi possível abrir a solicitação.'); return }
      setD(r.data)
      carregarConversa(id)
      carregarAcompanhantes(id)
      if (r.data.podeGerenciar) services.api.solicitacoesAtendentes().then((g: any) => { if (vivo && !g.fail) setGestores(g.data) })
    })
    return () => { vivo = false }
  }, [id, carregarConversa, carregarAcompanhantes])

  // A triagem acompanha o que o servidor guardou: quando o detalhe muda (salvou, mudou de status), o formulário volta a bater.
  useEffect(() => { if (d) setTriagem(triagemDe(d)) }, [d])

  const linha: Item[] = useMemo(() => [
    ...mensagens.map(m => ({ quando: m.criadoEm, msg: m })),
    ...historico.map(h => ({ quando: h.criadoEm, hist: h }))
  ].sort((a, b) => a.quando.localeCompare(b.quando)), [mensagens, historico])

  if (id == null) return null

  const enviar = async () => {
    const t = texto.trim()
    if (!t || enviando) return
    setEnviando(true)
    const r = await services.api.solicitacaoEnviarMensagem(id, t, interna)
    setEnviando(false)
    if (r.fail) { notify(r.error || 'Não foi possível enviar a mensagem.', { variant: 'error' }); return }
    setTexto('')
    carregarConversa(id)
  }

  // Toda ação do atendimento devolve o detalhe novo; a conversa é recarregada porque o histórico ganhou uma linha.
  const aplicar = async (chamada: Promise<any>, sucesso: string): Promise<boolean> => {
    setAgindo(true)
    const r = await chamada
    setAgindo(false)
    if (r.fail) { notify(r.error || 'Não foi possível concluir.', { variant: 'error' }); return false }
    setD(r.data)
    carregarConversa(id)
    notify(sucesso, { variant: 'success' })
    return true
  }

  const processado = (r: ResultadoProcessar) => {
    setProcessando(false)
    setD(r.solicitacao)
    carregarConversa(id)
    notify(`Acesso aplicado: ${r.acessosCriados} acessos, ${r.equipesAdicionadas} equipes e ${r.regrasCriadas} valores de regra. Solicitação concluída.`, { variant: 'success' })
    if (r.abreVazio.length > 0)
      notify(`Atenção: ${r.abreVazio.length === 1 ? 'um dashboard abre vazio' : `${r.abreVazio.length} dashboards abrem vazios`} para a pessoa por falta de valor na regra.`, { variant: 'warning' })
  }

  const abrirMotivo = (acao: AcaoComMotivo) => { setMotivo(''); setMotivoPara(acao) }

  const mudarStatus = (novo: string) => {
    if (!d || novo === d.status) return
    if (novo === 'cancelado') { abrirMotivo('cancelar'); return }
    aplicar(services.api.solicitacaoMudarStatus(id, novo), `Status: ${rotuloStatus(novo)}.`)
  }

  const mudarResponsavel = (valor: string) =>
    aplicar(services.api.solicitacaoResponsavel(id, valor ? Number(valor) : null), valor ? 'Responsável definido.' : 'Responsável retirado.')

  const confirmarMotivo = async () => {
    if (!motivoPara) return
    const chamada = motivoPara === 'cancelar' ? services.api.solicitacaoMudarStatus(id, 'cancelado', motivo.trim())
      : motivoPara === 'recusar' ? services.api.solicitacaoRecusar(id, motivo.trim())
        : motivoPara === 'bloquear' ? services.api.solicitacaoBloquear(id, true, motivo.trim())
          : services.api.solicitacaoReabrir(id, motivo.trim())
    const texto = motivoPara === 'cancelar' ? 'Solicitação cancelada.' : motivoPara === 'recusar' ? 'Entrega recusada. Voltou para desenvolvimento.'
      : motivoPara === 'bloquear' ? 'Solicitação bloqueada.' : 'Solicitação reaberta.'
    if (await aplicar(chamada, texto)) setMotivoPara(null)
  }

  const subir = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivos = Array.from(e.target.files || [])
    e.target.value = ''
    if (!arquivos.length) return
    setSubindo(true)
    let restam = MAX_ANEXOS - anexos.length
    for (const a of arquivos) {
      const problema = conferirArquivo(a.name, a.size)
      if (problema) { notify(`${a.name}: ${problema}`, { variant: 'error' }); continue }
      if (restam <= 0) { notify(`Cada solicitação aceita até ${MAX_ANEXOS} anexos.`, { variant: 'error' }); break }
      const r = await services.api.solicitacaoEnviarAnexo(id, a)
      if (r.fail) notify(`${a.name}: ${r.error || 'não subiu.'}`, { variant: 'error' })
      else restam--
    }
    setSubindo(false)
    const a = await services.api.solicitacaoAnexos(id)
    if (!a.fail) setAnexos(a.data)
  }

  const abrirAnexo = async (a: Anexo, inline: boolean) => {
    const r = await services.api.solicitacaoLinkAnexo(id, a.id, inline)
    if (r.fail) { notify(r.error || 'Não foi possível abrir o anexo.', { variant: 'error' }); return }
    const link = document.createElement('a')
    link.href = r.data.url
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const removerAnexo = async (a: Anexo) => {
    const r = await services.api.solicitacaoRemoverAnexo(id, a.id)
    if (r.fail) { notify(r.error || 'Não foi possível remover o anexo.', { variant: 'error' }); return }
    setAnexos(l => l.filter(x => x.id !== a.id))
  }

  // Quem já vê a solicitação não aparece na busca: o servidor recusaria de qualquer jeito.
  const buscarParaCompartilhar = async (q: string) => {
    const r = await services.api.solicitacoesBuscar('acompanhantes', q)
    if (r.fail) return []
    return (r.data as Opcao[]).filter(p => p.id !== euId && p.id !== d?.solicitanteId && !acompanhantes.some(a => a.userId === p.id))
  }

  const incluirAcompanhantes = async (escolhidas: Opcao[]) => {
    const r = await services.api.solicitacaoIncluirAcompanhantes(id, escolhidas.map(o => o.id))
    if (r.fail) { notify(r.error || 'Não foi possível compartilhar.', { variant: 'error' }); return }
    setAcompanhantes(r.data)
    carregarConversa(id)
    notify(escolhidas.length === 1 ? `Compartilhado com ${escolhidas[0].nome}. A pessoa recebeu um aviso no sino.` : 'Compartilhado. As pessoas receberam um aviso no sino.', { variant: 'success' })
  }

  const tirarAcompanhante = async (a: Acompanhante) => {
    const r = await services.api.solicitacaoTirarAcompanhante(id, a.userId)
    if (r.fail) { notify(r.error || 'Não foi possível tirar o acompanhante.', { variant: 'error' }); return }
    // Quem só acompanhava e saiu deixa de ver a solicitação: não há mais o que mostrar.
    if (a.userId === euId && !!d && soAcompanha(d)) { notify('Você deixou de acompanhar esta solicitação.', { variant: 'success' }); aoFechar(); return }
    carregarAcompanhantes(id)
    carregarConversa(id)
  }

  const status = d ? STATUS[d.status] : null
  const escolhiveis = d ? statusParaEscolher(d) : []
  const mudouTriagem = !!d && (triagem.prioridade !== d.prioridade || triagem.prazo !== (d.prazo ? d.prazo.slice(0, 10) : '') || triagem.esforco !== (d.esforco || ''))
  const abertaParaTriar = !!d && d.podeGerenciar && !encerrado(d.status)
  const responsavelForaDaLista = !!d?.responsavelId && !gestores.some(g => g.userId === d.responsavelId)
  const janelaDoMotivo = motivoPara ? JANELA_DO_MOTIVO[motivoPara] : null
  const linhasPedido = d ? linhasDoPedido(d.tipo, d.dados) : []

  return (
    <>
    <div className="nv-veu" onMouseDown={e => { if (e.target === e.currentTarget) aoFechar() }} onKeyDown={e => { if (e.key === 'Escape') aoFechar() }}>
      <div className="nv sol-detalhe" role="dialog" aria-modal="true" aria-labelledby="sol-titulo">
        <div className="nv-cab">
          <div>
            <h2 id="sol-titulo">{d ? `#${d.id} · ${d.titulo}` : 'Solicitação'}</h2>
            {d && <small>{rotuloTipo(d.tipo)} · de {d.solicitanteNome || 'alguém'} em {dataHora(d.criadoEm)}</small>}
          </div>
          <button type="button" className="nv-ico" onClick={aoFechar} aria-label="Fechar"><CloseIcon /></button>
        </div>

        <div className="nv-corpo sol-corpo">
          {erro && <div className="nv-erro" role="alert">{erro}</div>}
          {!d && !erro && <p className="ng-explic">Carregando…</p>}

          {d && (<>
            <div className="sol-selos">
              <span className={`ng-pill ${status?.tom || ''}`}>{rotuloStatus(d.status)}</span>
              <span className="ng-pill">Prioridade {rotuloPrioridade(d.prioridade).toLowerCase()}</span>
              {d.prazo && <span className={`ng-pill ${atrasada(d) ? 'inativo' : ''}`}>{atrasada(d) ? 'Atrasada · ' : 'Prazo '}{dataDoPrazo(d.prazo)}</span>}
              {d.bloqueado && <span className="ng-pill alerta">Bloqueada{d.bloqueioMotivo ? `: ${d.bloqueioMotivo}` : ''}</span>}
              <span className="ng-pill">{d.responsavelNome ? `Atendida por ${d.responsavelNome}` : 'Sem responsável ainda'}</span>
            </div>

            {soAcompanha(d) && (
              <div className="sol-aviso" role="note">
                {d.solicitanteNome || 'Quem pediu'} compartilhou esta solicitação com você. Aqui você acompanha, conversa e anexa; quem decide é quem pediu e o time de BI.
              </div>
            )}

            {(d.podeGerenciar || podeAceitarOuRecusar(d) || podeReabrir(d) || podeProcessar(d)) && (
              <section className="sol-atend">
                <h3 className="sol-h">Atendimento</h3>
                {d.podeGerenciar && (
                  <div className="sol-duas">
                    <label className="ng-campo"><span>Status</span>
                      <select value={d.status} onChange={e => mudarStatus(e.target.value)} disabled={agindo || escolhiveis.length === 0} aria-label="Status">
                        {Object.keys(STATUS).filter(k => k === d.status || escolhiveis.includes(k)).map(k => <option key={k} value={k}>{rotuloStatus(k)}</option>)}
                      </select>
                    </label>
                    <label className="ng-campo"><span>Responsável</span>
                      <select value={d.responsavelId ?? ''} onChange={e => mudarResponsavel(e.target.value)} disabled={agindo || escolhiveis.length === 0} aria-label="Responsável">
                        <option value="">Ninguém</option>
                        {responsavelForaDaLista && <option value={d.responsavelId!}>{d.responsavelNome || `Pessoa #${d.responsavelId}`}</option>}
                        {gestores.map(g => <option key={g.userId} value={g.userId}>{g.nome}</option>)}
                      </select>
                    </label>
                  </div>
                )}
                {abertaParaTriar && (<>
                  <div className="sol-tres">
                    <label className="ng-campo"><span>Prioridade</span>
                      <select value={triagem.prioridade} onChange={e => setTriagem({ ...triagem, prioridade: e.target.value })} disabled={agindo} aria-label="Prioridade">
                        {PRIORIDADES.map(p => <option key={p.id} value={p.id}>{p.rotulo}</option>)}
                      </select>
                    </label>
                    <label className="ng-campo"><span>Prazo</span>
                      <input type="date" value={triagem.prazo} onChange={e => setTriagem({ ...triagem, prazo: e.target.value })} disabled={agindo} aria-label="Prazo" />
                    </label>
                    <label className="ng-campo"><span>Esforço</span>
                      <select value={triagem.esforco} onChange={e => setTriagem({ ...triagem, esforco: e.target.value })} disabled={agindo} aria-label="Esforço">
                        {ESFORCOS.map(x => <option key={x.id} value={x.id}>{x.rotulo}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="sol-atend-botoes">
                    <button type="button" className="ng-btn" disabled={agindo || !mudouTriagem}
                            onClick={() => aplicar(services.api.solicitacaoTriagem(id, corpoDaTriagem(triagem)), 'Triagem salva.')}>Salvar triagem</button>
                    {d!.bloqueado
                      ? <button type="button" className="ng-btn" disabled={agindo} onClick={() => aplicar(services.api.solicitacaoBloquear(id, false, null), 'Bloqueio retirado.')}>Desbloquear</button>
                      : <button type="button" className="ng-btn" disabled={agindo} onClick={() => abrirMotivo('bloquear')}>Bloquear</button>}
                  </div>
                </>)}
                {d.podeGerenciar && gestores.length === 0 && escolhiveis.length > 0 && (
                  <small className="sol-ajuda">Ninguém tem a permissão gestao_solicitacoes no AuthManager ainda. Quem atende é quem tem essa permissão.</small>
                )}
                {(podeAceitarOuRecusar(d) || podeReabrir(d) || podeProcessar(d)) && (
                  <div className="sol-atend-botoes">
                    {podeProcessar(d) && <button type="button" className="ng-btn prim" disabled={agindo} onClick={() => setProcessando(true)}>Processar</button>}
                    {podeAceitarOuRecusar(d) && (<>
                      <button type="button" className="ng-btn prim" disabled={agindo}
                              onClick={() => aplicar(services.api.solicitacaoAceitar(id), 'Entrega aceita. Solicitação concluída.')}>Aceitar entrega</button>
                      <button type="button" className="ng-btn" disabled={agindo} onClick={() => abrirMotivo('recusar')}>Recusar entrega</button>
                    </>)}
                    {podeReabrir(d) && <button type="button" className="ng-btn" disabled={agindo} onClick={() => abrirMotivo('reabrir')}>Reabrir</button>}
                  </div>
                )}
              </section>
            )}

            <section>
              <h3 className="sol-h">O que foi pedido</h3>
              <p className="sol-texto">{d.descricao}</p>
              {linhasPedido.length > 0 && (
                <dl className="sol-dados">
                  {linhasPedido.map(l => <React.Fragment key={l.rotulo}><dt>{l.rotulo}</dt><dd>{l.valor}</dd></React.Fragment>)}
                </dl>
              )}
            </section>

            <section>
              <h3 className="sol-h">Quem acompanha <small>({acompanhantes.length})</small></h3>
              {acompanhantes.length === 0 && <p className="ng-explic">Só quem pediu e o time de BI veem esta solicitação.</p>}
              <ul className="sol-arquivos">
                {acompanhantes.map(a => (
                  <li key={a.userId}>
                    <b>{a.nome || `Pessoa #${a.userId}`}</b> <small>incluído por {a.adicionadoPorNome || 'alguém'} em {dataHora(a.criadoEm)}</small>
                    {a.podeTirar && <button type="button" onClick={() => tirarAcompanhante(a)}>{a.userId === euId ? 'sair' : 'tirar'}</button>}
                  </li>
                ))}
              </ul>
              <CampoBusca rotulo="Compartilhar com" dica="Buscar por nome ou e-mail" buscar={buscarParaCompartilhar} valor={[]} aoMudar={incluirAcompanhantes} />
              <small className="sol-ajuda">Quem você incluir vê, conversa e anexa, e recebe os avisos no sino. Não decide nada: aceitar, recusar e reabrir ficam com quem pediu.</small>
            </section>

            <section>
              <h3 className="sol-h">Anexos <small>({anexos.length}/{MAX_ANEXOS})</small></h3>
              {anexos.length === 0 && <p className="ng-explic">Nenhum anexo.</p>}
              <ul className="sol-arquivos">
                {anexos.map(a => (
                  <li key={a.id}>
                    <b>{a.nome}</b> <small>{tamanhoLegivel(a.tamanho)} · {a.autorNome || 'alguém'}</small>
                    <button type="button" onClick={() => abrirAnexo(a, false)}>baixar</button>
                    {a.abreNoNavegador && <button type="button" onClick={() => abrirAnexo(a, true)}>abrir</button>}
                    {(d.podeGerenciar || a.autorId === euId) && <button type="button" onClick={() => removerAnexo(a)}>remover</button>}
                  </li>
                ))}
              </ul>
              <button type="button" className="ng-btn" onClick={() => seletor.current?.click()} disabled={subindo || anexos.length >= MAX_ANEXOS}>
                {subindo ? 'Enviando…' : 'Anexar arquivo'}
              </button>
              <input ref={seletor} type="file" multiple hidden onChange={subir} aria-label="Anexar arquivo" />
            </section>

            <section>
              <h3 className="sol-h">Conversa e histórico</h3>
              <ol className="sol-linha">
                {linha.map(i => i.msg ? (
                  <li key={`m${i.msg.id}`} className={`sol-msg${i.msg.interna ? ' interna' : ''}${i.msg.autorId === euId ? ' minha' : ''}`}>
                    <div className="sol-msg-cab"><b>{i.msg.autorNome || 'Alguém'}</b><small>{dataHora(i.msg.criadoEm)}</small>{i.msg.interna && <span className="ng-pill alerta">Nota interna</span>}</div>
                    <p>{i.msg.mensagem}</p>
                  </li>
                ) : (
                  <li key={`h${i.hist!.id}`} className="sol-hist"><span>{i.hist!.usuarioNome || 'Alguém'} {textoDoHistorico(i.hist!).replace(/^./, c => c.toLowerCase())}</span><small>{dataHora(i.hist!.criadoEm)}</small></li>
                ))}
                {linha.length === 0 && <li className="sol-hist"><span>Ainda sem mensagens.</span></li>}
              </ol>
              <div className="sol-escrever">
                <textarea rows={3} value={texto} maxLength={4000} aria-label="Mensagem"
                          placeholder={interna ? 'Nota interna: só a equipe de BI enxerga' : 'Escreva uma mensagem'}
                          onChange={e => setTexto(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); enviar() } }} />
                <div className="sol-escrever-rodape">
                  {d.podeGerenciar && <label className="sol-interna"><input type="checkbox" checked={interna} onChange={e => setInterna(e.target.checked)} /> Nota interna (o solicitante não vê)</label>}
                  <span style={{ flex: 1 }} />
                  <button type="button" className="nv-btn prim" onClick={enviar} disabled={!texto.trim() || enviando}>{enviando ? 'Enviando…' : 'Enviar'}</button>
                </div>
              </div>
            </section>
          </>)}
        </div>
      </div>
    </div>
    <ProcessarSolicitacao id={processando ? id : null} aoFechar={() => setProcessando(false)} aoProcessar={processado} />
    <Janela aberto={!!motivoPara} titulo={janelaDoMotivo?.titulo || ''} sub={d ? `#${d.id} · ${d.titulo}` : undefined} botao={janelaDoMotivo?.botao || ''}
            perigo={motivoPara === 'cancelar'} desabilitado={motivoPara !== 'reabrir' && !motivoValido(motivo)} salvando={agindo}
            aoConfirmar={confirmarMotivo} aoFechar={() => setMotivoPara(null)}>
      <label className="ng-campo"><span>{janelaDoMotivo?.rotulo}</span>
        <textarea rows={3} value={motivo} maxLength={1000} onChange={e => setMotivo(e.target.value)} aria-label={janelaDoMotivo?.rotulo} />
      </label>
    </Janela>
    </>
  )
}
